import { AloftPrice, isDefined } from '@aloft/toolkit-shared';
import { Flex, IconButton } from '@aloft/toolkit-sui';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useMemo } from 'react';

import { TwoLineCell } from '../Grid/TwoLineCell.tsx';
import { VacantLandSalesProperty } from './types.ts';

const INITIAL_PAGE_INDEX = 0;

export const useVacantLandSalesTable = (
  data: VacantLandSalesProperty[],
  orderID: string,
  pageSize: number,
) => {
  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<VacantLandSalesProperty>();

    return [
      columnHelper.accessor(
        (property) =>
          `${property.address?.AddressLineText}, ${property.address?.CityName}, ${property.address?.StateCode} ${property.address?.PostalCode}`,
        {
          id: 'Address',
          cell: ({ row: { original: row } }) => (
            <Flex justifyContent="space-between">
              <TwoLineCell
                line1={row.address?.AddressLineText ?? ''}
                line2={`${row.address?.CityName}, ${row.address?.StateCode} ${row.address?.PostalCode}`}
              />
              {row.listingID && (
                <IconButton
                  icon="external-link"
                  variant="tertiary"
                  onClick={() => {
                    window.open(
                      `/reference-data/${orderID}/craftsman/mls-listing/${row.listingID}`,
                      '_blank',
                      'rel=noopener noreferrer',
                    );
                  }}
                  ariaLabel="close window"
                  iconSize="lg"
                  size="sm"
                />
              )}
            </Flex>
          ),
        },
      ),
      columnHelper.accessor((property) => property.distanceToSubject, {
        id: 'Proximity',
        cell: ({ row: { original: row } }) =>
          row.id === 'subject' ? '' : `${row.distanceToSubject ?? '-'} miles`,
      }),
      columnHelper.accessor((property) => property.lotSize?.toSqFtValueStringRounded(), {
        id: 'Lot Size',
        cell: ({ row: { original: row } }) => row.lotSize?.toSqFtValueStringRounded(),
      }),
      columnHelper.accessor((property) => property.salePrice?.toNumericPrice(), {
        id: 'Sale Price',
        cell: ({ row: { original: row } }) => row.salePrice?.toString(true),
      }),
      columnHelper.accessor(
        (property) =>
          (property.salePrice?.toNumericPrice() ?? 0) /
          (property.lotSize?.toSqFt().unitAmount ?? 1),
        {
          id: '$ / SF',
          cell: ({ row: { original: row } }) =>
            AloftPrice.fromNumericPrice(
              (row.salePrice?.toNumericPrice() ?? 0) / (row.lotSize?.toSqFt().unitAmount ?? 1),
            ).toString(),
        },
      ),
      columnHelper.accessor((property) => property.saleDate, {
        id: 'Sale Date',
        cell: ({ row: { original: row } }) => row.saleDate?.format('MM/yy'),
      }),
    ].filter(isDefined);
  }, [orderID]);

  const table = useReactTable({
    data,
    columns,
    enableSorting: false,
    getRowId: (row, index) => `${row.id}-${index}`,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageIndex: INITIAL_PAGE_INDEX,
        pageSize,
      },
    },
  });

  return { table };
};
